import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AuthLayout, ThemeToggle } from '@lnr-v2/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import { invitationService } from '../../services/invitationService';
import type { VerifyInvitationResponse } from '@lnr-v2/types';
import { PasswordStrengthIndicator } from '../../components/PasswordStrengthIndicator';
import styles from './InvitationAcceptPage.module.css';

interface FormData {
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  password?: string;
  confirmPassword?: string;
}

export const InvitationAcceptPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { showSuccess, showError } = useToast();

  // Loading states
  const [isVerifying, setIsVerifying] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Invitation data
  const [invitationData, setInvitationData] =
    useState<VerifyInvitationResponse | null>(null);
  const [verificationError, setVerificationError] = useState<string | null>(
    null
  );

  // Form state
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<keyof FormData, boolean>>({
    firstName: false,
    lastName: false,
    password: false,
    confirmPassword: false,
  });

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerificationError('Invalid invitation link');
        setIsVerifying(false);
        return;
      }

      try {
        const response = await invitationService.verifyInvitation(token);

        if (response.valid && response.email) {
          setInvitationData(response);
        } else {
          setVerificationError(
            response.error || 'Invalid or expired invitation'
          );
        }
      } catch (error) {
        console.error('Error verifying invitation:', error);
        setVerificationError('Failed to verify invitation. Please try again.');
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  // Form validation
  const validateForm = (): FormErrors => {
    const errors: FormErrors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    return errors;
  };

  // Handle input changes
  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Handle input blur
  const handleInputBlur = (field: keyof FormData) => {
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));

    // Validate single field on blur
    const errors = validateForm();
    if (errors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: errors[field],
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      firstName: true,
      lastName: true,
      password: true,
      confirmPassword: true,
    });

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (!token) {
      showError('Invalid invitation link');
      return;
    }

    setIsSubmitting(true);

    try {
      await invitationService.completeInvitation({
        token,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        password: formData.password,
      });

      showSuccess('Account created successfully! Please log in.');

      // Small delay to ensure toast is visible before navigation
      setTimeout(() => {
        navigate('/admin/login', {
          state: { email: invitationData?.email },
        });
      }, 500);
    } catch (error) {
      console.error('Error completing invitation:', error);
      if (error instanceof Error) {
        showError(error.message || 'Failed to create account');
      } else {
        showError('Failed to create account. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render loading state
  if (isVerifying) {
    return (
      <AuthLayout
        theme={theme}
        themeToggle={<ThemeToggle theme={theme} onToggle={toggleTheme} />}
      >
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.loadingContainer}>
              <div className={styles.spinner} />
              <p className={styles.loadingText}>Verifying invitation...</p>
            </div>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Render error state
  if (verificationError || !invitationData?.valid) {
    return (
      <AuthLayout
        theme={theme}
        themeToggle={<ThemeToggle theme={theme} onToggle={toggleTheme} />}
      >
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.errorContainer}>
              <svg
                className={styles.errorIcon}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <h2 className={styles.errorTitle}>Invalid Invitation</h2>
              <p className={styles.errorMessage}>
                {verificationError ||
                  invitationData?.error ||
                  'This invitation link is invalid or has expired.'}
              </p>
              <div className={styles.errorActions}>
                <Link to="/admin/login" className={styles.linkButton}>
                  Go to Login
                </Link>
                <p className={styles.helpText}>
                  Need a new invitation? Please contact your administrator.
                </p>
              </div>
            </div>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Render form
  return (
    <AuthLayout
      theme={theme}
      themeToggle={<ThemeToggle theme={theme} onToggle={toggleTheme} />}
    >
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <h1 className={styles.title}>Complete Your Account Setup</h1>
            <p className={styles.subtitle}>
              Welcome! Please enter your details to create your account.
            </p>
          </div>

          <form onSubmit={handleSubmit} className={styles.form}>
            {/* Email (read-only) */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Email</label>
              <input
                type="email"
                className={`${styles.input} ${styles.readOnly}`}
                value={invitationData.email || ''}
                readOnly
                disabled
              />
              <p className={styles.fieldHelp}>
                This email was used to send you the invitation
              </p>
            </div>

            {/* First Name */}
            <div className={styles.formGroup}>
              <label htmlFor="firstName" className={styles.label}>
                First Name <span className={styles.required}>*</span>
              </label>
              <input
                id="firstName"
                type="text"
                className={`${styles.input} ${touched.firstName && formErrors.firstName ? styles.inputError : ''}`}
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                onBlur={() => handleInputBlur('firstName')}
                placeholder="Enter your first name"
                disabled={isSubmitting}
                autoComplete="given-name"
              />
              {touched.firstName && formErrors.firstName && (
                <span className={styles.fieldError}>
                  {formErrors.firstName}
                </span>
              )}
            </div>

            {/* Last Name */}
            <div className={styles.formGroup}>
              <label htmlFor="lastName" className={styles.label}>
                Last Name <span className={styles.required}>*</span>
              </label>
              <input
                id="lastName"
                type="text"
                className={`${styles.input} ${touched.lastName && formErrors.lastName ? styles.inputError : ''}`}
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                onBlur={() => handleInputBlur('lastName')}
                placeholder="Enter your last name"
                disabled={isSubmitting}
                autoComplete="family-name"
              />
              {touched.lastName && formErrors.lastName && (
                <span className={styles.fieldError}>{formErrors.lastName}</span>
              )}
            </div>

            {/* Password */}
            <div className={styles.formGroup}>
              <label htmlFor="password" className={styles.label}>
                Password <span className={styles.required}>*</span>
              </label>
              <input
                id="password"
                type="password"
                className={`${styles.input} ${touched.password && formErrors.password ? styles.inputError : ''}`}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                onBlur={() => handleInputBlur('password')}
                placeholder="Create a strong password"
                disabled={isSubmitting}
                autoComplete="new-password"
              />
              {formData.password && (
                <PasswordStrengthIndicator password={formData.password} />
              )}
              {touched.password && formErrors.password && (
                <span className={styles.fieldError}>{formErrors.password}</span>
              )}
            </div>

            {/* Confirm Password */}
            <div className={styles.formGroup}>
              <label htmlFor="confirmPassword" className={styles.label}>
                Confirm Password <span className={styles.required}>*</span>
              </label>
              <input
                id="confirmPassword"
                type="password"
                className={`${styles.input} ${touched.confirmPassword && formErrors.confirmPassword ? styles.inputError : ''}`}
                value={formData.confirmPassword}
                onChange={(e) =>
                  handleInputChange('confirmPassword', e.target.value)
                }
                onBlur={() => handleInputBlur('confirmPassword')}
                placeholder="Confirm your password"
                disabled={isSubmitting}
                autoComplete="new-password"
              />
              {touched.confirmPassword && formErrors.confirmPassword && (
                <span className={styles.fieldError}>
                  {formErrors.confirmPassword}
                </span>
              )}
            </div>

            {/* Role information */}
            {invitationData.role && (
              <div className={styles.roleInfo}>
                <span className={styles.roleLabel}>Account Role:</span>
                <span className={styles.roleBadge}>
                  {invitationData.role.replace('_', ' ')}
                </span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className={styles.buttonSpinner} />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </button>

            {/* Footer Links */}
            <div className={styles.footer}>
              <p className={styles.footerText}>
                Already have an account?{' '}
                <Link to="/admin/login" className={styles.link}>
                  Sign in
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </AuthLayout>
  );
};
